/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * VoterMapPanel — filterable Leaflet map for the Voters page.
 *
 * Fetches EVERY matching voter as a compact pin (GET /voters/map-pins,
 * ~60k for an unfiltered county) whenever filters/search change, debounced
 * 300ms with stale-request abort, and renders them on a clustered Leaflet
 * map with tier-colored dot markers.
 *
 * Two consequences of "every voter" shape this file:
 * - Pins carry no PII (id/lat/lng/tier/score only); the popup fetches
 *   GET /voters/{id} when a dot is clicked.
 * - Markers are built imperatively via leaflet.markercluster
 *   (PinClusterLayer below), not as react-leaflet <Marker> children —
 *   60k React component instances take seconds to reconcile, a plain
 *   loop over L.marker() with chunkedLoading takes a fraction of that.
 *
 * Designed to be mounted next to (or toggled with) the voters DataTable.
 * Does not own filter state — it reads `filters` + `search` from the parent
 * (typically VotersView's `useListQuery` hook).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Popup, useMap } from 'react-leaflet';
import FullscreenControl from '@/components/map/FullscreenControl';
import LassoControl, { type LassoLatLng } from '@/components/map/LassoControl';
import CreateRouteModal from '@/components/map/CreateRouteModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import client from '@/api/client';
import {
  fetchMapPins,
  fetchVotersWithin,
  fetchKnockStatusForFilters,
  fetchVoterPopupDetail,
  type MapPin,
  type VoterPopupDetail,
  type VotersWithinResponse,
  type KnockStatusResponse,
} from '@/api/voters';
import MapLegend from '@/components/map/MapLegend';
import {
  KNOCK_LEGEND,
  TIER_LEGEND,
  clusterHtml,
  dominant,
  dotHtml,
  knockStyle,
  tierFor,
  type KnockState,
  type LegendEntry,
} from '@/lib/mapColors';

interface VoterMapPanelProps {
  filters: Record<string, unknown>;
  search: string;
}

// Platte County, MO fallback center — matches InteractiveMap.tsx default
const DEFAULT_CENTER: [number, number] = [39.3698, -94.7720];
const DEFAULT_ZOOM = 10;

// Tier colors now come from `@/lib/mapColors`, which derives them from
// glossary.ts's TIER_PALETTE — the palette users actually read in the legend
// and Glossary page. The local table this replaces disagreed with both the
// Glossary and InteractiveMap.tsx (anchor was red here, green there), so the
// same voter changed color depending on which map you were looking at.

const TIER_PILL_BG: Record<string, string> = {
  anchor: 'bg-tier-anchor/20 text-tier-anchor border-tier-anchor/40',
  hot: 'bg-tier-hot/20 text-tier-hot border-tier-hot/40',
  warm: 'bg-tier-warm/20 text-tier-warm border-tier-warm/40',
  cool: 'bg-tier-cool/20 text-tier-cool border-tier-cool/40',
  super_voter: 'bg-status-success-bg/20 text-status-success border-status-success-bg/40',
};

/** The legend entry a dot should be drawn from, under the current scheme. */
function entryFor(
  colorBy: 'tier' | 'knock',
  voter: any,
  knockState: KnockState | undefined,
): LegendEntry {
  if (colorBy === 'knock') return knockStyle(knockState);
  const key = tierFor(voter.engagement_tier, voter.score);
  return (
    TIER_LEGEND.find((e) => e.key === key) ?? TIER_LEGEND[TIER_LEGEND.length - 1]
  );
}

/**
 * The legend key is carried in the icon's className (`dot-<key>`) so the
 * cluster bubble can recover it from its children.
 *
 * Via the ICON specifically, not as a prop on <Marker>: react-leaflet does not
 * reliably forward unknown props into Leaflet's marker options, so an
 * `entryKey` prop arrived as undefined and every cluster silently fell back to
 * the first legend entry — painting a fully-planned district as "not on a
 * route". `icon` is a first-class prop and always survives.
 */
function makeDotIcon(entry: LegendEntry): L.DivIcon {
  return L.divIcon({
    className: `voter-map-dot dot-${entry.key}`,
    html: dotHtml(entry, 14),
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -7],
  });
}

/** Recover the legend key a marker was drawn from. */
function entryKeyOf(marker: any): string | undefined {
  const cls: string = marker?.options?.icon?.options?.className ?? '';
  return cls.split(/\s+/).find((c) => c.startsWith('dot-'))?.slice(4);
}

/**
 * The clustered pin layer, built imperatively with leaflet.markercluster.
 *
 * NOT react-leaflet `<Marker>` children on purpose: this layer renders every
 * matching voter (~60k unfiltered), and 60k React component instances take
 * seconds of main-thread time to reconcile before Leaflet even sees them. A
 * plain loop over `L.marker()` plus `addLayers` with `chunkedLoading` builds
 * the same layer in a fraction of that, in timeslices that keep the tab
 * responsive. The trade is manual lifecycle management, which is this
 * component's whole job.
 *
 * Rebuilt whole on pins/scheme/overlay change — that rebuild replaces the
 * old `key={...}` remount hack, and matters for the same reason it did:
 * MarkerClusterGroup caches its cluster bubbles, so recoloring in place
 * would leave bubbles in the old palette while the dots change.
 */
function PinClusterLayer({
  pins,
  colorBy,
  knockByVoter,
  onPinClick,
}: {
  pins: MapPin[];
  colorBy: 'tier' | 'knock';
  knockByVoter: Map<number, KnockState>;
  onPinClick: (pin: MapPin) => void;
}) {
  const map = useMap();
  // Read at click time so the rebuild effect doesn't depend on callback
  // identity — a parent re-render must not rebuild 60k markers.
  const onPinClickRef = useRef(onPinClick);
  onPinClickRef.current = onPinClick;

  useEffect(() => {
    const order = colorBy === 'knock' ? KNOCK_LEGEND : TIER_LEGEND;
    const byKey = new Map(order.map((e) => [e.key, e]));

    // One DivIcon per legend entry, shared by every marker drawn from it —
    // tens of thousands of markers do not need that many identical icons.
    const iconCache = new Map<string, L.DivIcon>();
    const iconFor = (entry: LegendEntry): L.DivIcon => {
      let icon = iconCache.get(entry.key);
      if (!icon) {
        icon = makeDotIcon(entry);
        iconCache.set(entry.key, icon);
      }
      return icon;
    };

    const group = (L as any).markerClusterGroup({
      maxClusterRadius: 80,
      chunkedLoading: true,
      /**
       * Color a cluster bubble by the dominant state of its children.
       *
       * Not a nicety. At the default zoom of 10 nearly everything on this
       * map IS a cluster, so leaving clusters in Leaflet's default blue
       * hides the entire visualization behind uniform bubbles. Each child
       * marker carries the key of the legend entry it was drawn from, on its
       * icon's className; reading it back — rather than re-deriving the
       * color here — is what keeps a bubble from ever disagreeing with the
       * dots inside it.
       */
      iconCreateFunction: (cluster: any) => {
        const resolved = cluster
          .getAllChildMarkers()
          .map((m: any) => byKey.get(entryKeyOf(m) as string))
          .filter(Boolean) as LegendEntry[];
        const top = dominant(resolved, order) ?? order[0];
        return L.divIcon({
          className: 'voter-map-cluster',
          html: clusterHtml(top.color, cluster.getChildCount()),
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
      },
    });

    const markers = pins.map((p) => {
      const entry = entryFor(
        colorBy,
        { engagement_tier: p.tier, score: p.score },
        knockByVoter.get(p.id)
      );
      const marker = L.marker([p.lat, p.lng], { icon: iconFor(entry) });
      marker.on('click', () => onPinClickRef.current(p));
      return marker;
    });
    group.addLayers(markers);
    map.addLayer(group);

    return () => {
      map.removeLayer(group);
    };
  }, [map, pins, colorBy, knockByVoter]);

  return null;
}

/**
 * Popup body for a clicked pin. Pins are PII-free by design, so the name and
 * address are fetched here, when someone asks about one specific door —
 * tier and score render immediately from the pin while that's in flight.
 */
function PinPopupContent({ pin }: { pin: MapPin }) {
  const [detail, setDetail] = useState<VoterPopupDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setFailed(false);
    fetchVoterPopupDetail(pin.id)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [pin.id]);

  const tier = detail?.engagement_tier ?? pin.tier;
  const score = detail?.score ?? pin.score;
  const tierClass = (tier && TIER_PILL_BG[tier]) || TIER_PILL_BG.cool;

  return (
    <div className="text-sm w-56">
      {detail ? (
        <>
          <div className="font-bold text-slate-900">
            {detail.last_name}, {detail.first_name}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">{detail.address}</div>
        </>
      ) : failed ? (
        <div className="text-xs text-slate-600">
          Couldn't load this voter's details.
        </div>
      ) : (
        <div className="text-xs text-slate-500">Loading…</div>
      )}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tierClass}`}
        >
          {tier || 'unclassified'}
        </span>
        <span className="text-xs text-slate-700">
          Score: <span className="font-semibold">{Math.round(score)}</span>
        </span>
        {detail?.precinct_num != null && (
          <span className="text-xs text-slate-400">P{detail.precinct_num}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Encode just the filter/search set, with no pagination or response-shape
 * concerns mixed in.
 * - Arrays → comma-joined under `<key>__in`
 * - null/undefined/'' → dropped
 * - Everything else → passed through as-is (caller uses backend-native names)
 *
 * Shared by the marker fetch (as query params) and the lasso selection (as a
 * request body), so a lasso counts against exactly the filter the user can
 * see applied on the map.
 */
function buildFilterParams(
  filters: Record<string, unknown>,
  search: string
): Record<string, any> {
  const params: Record<string, any> = {};
  for (const [key, val] of Object.entries(filters || {})) {
    if (val === null || val === undefined || val === '') continue;
    if (Array.isArray(val)) {
      if (val.length === 0) continue;
      params[`${key}__in`] = val.join(',');
    } else {
      params[key] = val;
    }
  }
  if (search && search.trim() !== '') {
    params.q = search.trim();
  }
  return params;
}

// (The pins fetch, the knock overlay, and the lasso all take the bare
// buildFilterParams output — the server forces has_coords and owns the cap,
// so there is no pagination envelope to add anymore.)

/** True for the "a newer request superseded this one" family of errors. */
function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

const VoterMapPanel: React.FC<VoterMapPanelProps> = ({ filters, search }) => {
  const [pins, setPins] = useState<MapPin[]>([]);
  const [total, setTotal] = useState<number>(0);
  // True when the server capped the pin set (giant tenant / no filters).
  const [pinsTruncated, setPinsTruncated] = useState<boolean>(false);
  // The dot someone clicked — drives the single lazy-loading popup.
  const [activePin, setActivePin] = useState<MapPin | null>(null);
  // Matching voters IGNORING has_coords, so `totalAll - total` is the number of
  // voters this map physically cannot show. Null while unknown/failed — the
  // footer stays silent rather than claiming zero.
  const [totalAll, setTotalAll] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);

  // --- what the dots mean ---
  // 'tier' is the historical behavior and stays the default: a coordinator
  // opening the voters map is usually asking "who is worth talking to", not
  // "how far did we get". Knock status is a deliberate switch into field-
  // progress mode, and it costs an extra request, so it is opt-in.
  const [colorBy, setColorBy] = useState<'tier' | 'knock'>('tier');
  const [knock, setKnock] = useState<KnockStatusResponse | null>(null);
  const [knockLoading, setKnockLoading] = useState(false);
  const [knockError, setKnockError] = useState<string | null>(null);

  // --- lasso selection ---
  const [lassoArmed, setLassoArmed] = useState<boolean>(false);
  const [lassoPolygon, setLassoPolygon] = useState<LassoLatLng[] | null>(null);
  const [selection, setSelection] = useState<VotersWithinResponse | null>(null);
  const [selectionLoading, setSelectionLoading] = useState<boolean>(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectionAbortRef = useRef<AbortController | null>(null);
  const lassoPolygonRef = useRef<LassoLatLng[] | null>(null);
  lassoPolygonRef.current = lassoPolygon;

  // Latest filter state, read at request time so the selection callbacks can
  // stay identity-stable without going stale.
  const filterStateRef = useRef({ filters, search });
  filterStateRef.current = { filters, search };

  // Stable key for filter identity — triggers the effect only on real changes
  const paramsKey = useMemo(
    () => JSON.stringify(buildFilterParams(filters, search)),
    [filters, search]
  );

  const runSelection = useCallback(async (ring: LassoLatLng[]) => {
    if (selectionAbortRef.current) selectionAbortRef.current.abort();
    const ctrl = new AbortController();
    selectionAbortRef.current = ctrl;

    setSelectionLoading(true);
    setSelectionError(null);

    const { filters: f, search: s } = filterStateRef.current;
    try {
      const data = await fetchVotersWithin(
        // GeoJSON axis order — [lng, lat], the transpose of Leaflet's LatLng.
        ring.map((p) => [p.lng, p.lat] as [number, number]),
        buildFilterParams(f, s),
        ctrl.signal
      );
      setSelection({
        total: typeof data?.total === 'number' ? data.total : 0,
        voter_ids: Array.isArray(data?.voter_ids) ? data.voter_ids : [],
        truncated: Boolean(data?.truncated),
      });
      setSelectionLoading(false);
    } catch (err: any) {
      if (isAbortError(err)) return;
      setSelection(null);
      setSelectionError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to select voters in the lasso'
      );
      setSelectionLoading(false);
    }
  }, []);

  const handleLassoComplete = useCallback(
    (ring: LassoLatLng[]) => {
      setLassoPolygon(ring);
      void runSelection(ring);
    },
    [runSelection]
  );

  const clearLasso = useCallback(() => {
    if (selectionAbortRef.current) selectionAbortRef.current.abort();
    setLassoArmed(false);
    setLassoPolygon(null);
    setSelection(null);
    setSelectionError(null);
    setSelectionLoading(false);
  }, []);

  // A filter change while a lasso is up would otherwise leave the footer
  // showing a count for a filter that is no longer applied. Re-count against
  // the same shape instead, debounced like the marker fetch.
  useEffect(() => {
    const ring = lassoPolygonRef.current;
    if (!ring) return;
    const t = setTimeout(() => void runSelection(ring), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Cleanup any in-flight selection on unmount
  useEffect(() => {
    return () => {
      if (selectionAbortRef.current) selectionAbortRef.current.abort();
    };
  }, []);

  // Count the voters the map CANNOT show.
  //
  // The pins endpoint requires coordinates, so every pin it returns is
  // plottable by construction — which is why the old "missing coordinates"
  // readout was structurally incapable of being non-zero and simply always said
  // 0. That is worse than saying nothing: it affirmatively told a coordinator
  // nothing was missing while ~17k ungeocoded voters were excluded from both the
  // map and the lasso. `/voters/within` requires lat/lng too, so these voters
  // cannot be lassoed at any zoom and will never appear in a walk list.
  //
  // Same filters against GET /voters (which does NOT force coords),
  // page_size 1 — we want the envelope's total, not the rows.
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      client
        .get('/voters', {
          params: {
            envelope: 'v2',
            page_size: 1,
            ...buildFilterParams(filters, search),
          },
          signal: ctrl.signal,
        })
        .then((res) => {
          const t2 = res.data?.total;
          setTotalAll(typeof t2 === 'number' ? t2 : null);
        })
        .catch(() => {
          // Leave it unknown rather than guess — the footer hides the readout.
          setTotalAll(null);
        });
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, reloadKey]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setLoading(true);
      setError(null);

      fetchMapPins(buildFilterParams(filters, search), ctrl.signal)
        .then((data) => {
          setPins(Array.isArray(data?.pins) ? data.pins : []);
          setTotal(typeof data?.total === 'number' ? data.total : 0);
          setPinsTruncated(Boolean(data?.truncated));
          // A popup for a pin from the previous filter set would point at a
          // dot that may no longer exist.
          setActivePin(null);
          setLoading(false);
        })
        .catch((err: any) => {
          // Ignore aborts — a new request is in flight
          if (isAbortError(err)) return;
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              'Failed to load voters for map'
          );
          setLoading(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, reloadKey]);

  // Cleanup any in-flight request on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // The server guarantees coordinates on every pin; the filter is a cheap
  // belt-and-suspenders against a malformed row poisoning Leaflet.
  const plotted = useMemo(
    () =>
      pins.filter(
        (p) =>
          typeof p.lat === 'number' &&
          typeof p.lng === 'number' &&
          !Number.isNaN(p.lat) &&
          !Number.isNaN(p.lng)
      ),
    [pins]
  );

  /**
   * Fetch the knock overlay for the pins on screen — by FILTER, not by id
   * list: the server resolves the same filter set with the same plumbing,
   * order, and cap as the pins endpoint, so the overlay covers exactly what
   * the map fetched. (Posting the plotted ids back stopped being possible
   * when the map went from 500 pins to all of them — id mode caps at 500.)
   * Skipped entirely while colorBy is 'tier': the whole point of an overlay
   * is that it costs nothing when nobody is looking at it.
   */
  const knockAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (colorBy !== 'knock') {
      setKnock(null);
      setKnockError(null);
      return;
    }
    knockAbortRef.current?.abort();
    const ctrl = new AbortController();
    knockAbortRef.current = ctrl;
    setKnockLoading(true);
    setKnockError(null);
    const { filters: f, search: s } = filterStateRef.current;
    fetchKnockStatusForFilters(buildFilterParams(f, s), null, ctrl.signal)
      .then((data) => {
        setKnock(data);
        setKnockLoading(false);
      })
      .catch((err: any) => {
        if (
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED'
        )
          return;
        setKnock(null);
        // Say so rather than silently falling back to tier colors — a map
        // showing the wrong scheme without saying which is worse than an error.
        setKnockError(
          err?.response?.data?.detail ||
            'Could not load knock status for these doors.',
        );
        setKnockLoading(false);
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorBy, paramsKey]);

  useEffect(() => () => knockAbortRef.current?.abort(), []);

  /** voter_id -> knock state, for O(1) lookup while rendering markers. */
  const knockByVoter = useMemo(() => {
    const m = new Map<number, KnockState>();
    for (const e of knock?.entries ?? []) m.set(e.voter_id, e.state as KnockState);
    return m;
  }, [knock]);

  // Voters matching the filter that have no coordinates, so the map cannot plot
  // them and a lasso can never select them. Null = not yet known.
  const unmapped = totalAll === null ? null : Math.max(0, totalAll - total);

  const center = useMemo<[number, number]>(() => {
    if (plotted.length === 0) return DEFAULT_CENTER;
    let lat = 0;
    let lng = 0;
    for (const v of plotted) {
      lat += v.lat as number;
      lng += v.lng as number;
    }
    return [lat / plotted.length, lng / plotted.length];
  }, [plotted]);

  // Re-key the MapContainer when the derived center changes meaningfully so
  // Leaflet re-initializes centering. (MapContainer ignores prop updates to
  // `center` after first mount.)
  const mapKey = useMemo(
    () => `${center[0].toFixed(3)}-${center[1].toFixed(3)}`,
    [center]
  );

  // ============================================================================
  // Render
  // ============================================================================
  const footer = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
      <span>
        Showing{' '}
        <span className="text-status-success font-semibold">
          {plotted.length.toLocaleString()}
        </span>{' '}
        plotted
      </span>
      <span className="text-fg-muted">·</span>
      <span>
        <span className="text-fg-secondary font-semibold">
          {total.toLocaleString()}
        </span>{' '}
        total match
      </span>
      {unmapped !== null && unmapped > 0 && (
        <>
          <span className="text-fg-muted">·</span>
          <span
            title={
              `${unmapped.toLocaleString()} voters match this filter but have no ` +
              `coordinates. They are not on the map and a lasso cannot select ` +
              `them, so they will not appear in any route built from one.`
            }
          >
            <span className="text-status-warning font-semibold">
              {unmapped.toLocaleString()}
            </span>{' '}
            not mapped
          </span>
        </>
      )}
      {pinsTruncated && (
        <>
          <span className="text-fg-muted">·</span>
          <span
            className="text-status-warning"
            title={
              'The server caps one pin fetch; the counts above are still ' +
              'complete. Narrow the filters to see every matching dot.'
            }
          >
            showing the first {plotted.length.toLocaleString()} dots only
          </span>
        </>
      )}

      {lassoPolygon && (
        <>
          <span className="text-fg-muted">·</span>
          <span>
            {selectionLoading ? (
              <span className="text-fg-secondary">Counting selection…</span>
            ) : selectionError ? (
              <span className="text-status-danger">{selectionError}</span>
            ) : selection ? (
              <>
                {/* `total` is the server's count of every voter in the shape.
                    `voter_ids.length` is capped, so showing it here would
                    quietly under-report exactly where people trust the number. */}
                <span className="text-status-info font-semibold">
                  {selection.total.toLocaleString()}
                </span>{' '}
                selected by lasso
              </>
            ) : null}
          </span>

          {selection?.truncated && (
            <>
              <span className="text-fg-muted">·</span>
              <span className="text-status-warning">
                too many for one pass — only{' '}
                {selection.voter_ids.length.toLocaleString()} sent, and fewer are
                buildable
              </span>
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={clearLasso}
              className="px-2 py-1 rounded border border-border-base text-fg-secondary hover:bg-bg-elevated transition-colors"
            >
              Clear
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              disabled={selectionLoading || !selection?.voter_ids.length}
              className="px-2 py-1 rounded bg-accent text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Create Routes
            </button>
          </div>
        </>
      )}
    </div>
  );

  if (error) {
    return (
      <div className="rounded-lg border border-status-danger-bg/50 bg-status-danger-bg/10 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-status-danger">
              Couldn't load voter map
            </div>
            <div className="text-xs text-status-danger/80 mt-1">{error}</div>
          </div>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-xs rounded border border-status-danger-bg/50 text-status-danger hover:bg-status-danger-bg/20 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {createOpen && selection && (
        <CreateRouteModal
          voterIds={selection.voter_ids}
          totalSelected={selection.total}
          truncated={selection.truncated}
          onClose={() => setCreateOpen(false)}
          onCreated={(count) => {
            setCreateOpen(false);
            // The lasso has served its purpose; leaving it up invites building
            // the same doors twice, which no DELETE endpoint can undo.
            clearLasso();
            setCreatedNotice(
              `Created ${count} ${count === 1 ? 'route' : 'routes'}. Find them in Routes.`
            );
          }}
        />
      )}

      {createdNotice && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-status-success-bg/50 bg-status-success-bg/10 px-3 py-2 text-xs text-fg-primary">
          <span>{createdNotice}</span>
          <button
            onClick={() => setCreatedNotice(null)}
            className="text-fg-muted hover:text-fg-primary"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {footer}

      {/* Color scheme + legend. The legend is mandatory, not optional: the
          knock palette runs red/amber/green, the worst case for colorblind
          readers, and three of its states differ mainly by fill. Wraps rather
          than scrolls so it stays readable at 375px. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex shrink-0 items-center gap-1 rounded-md border border-border-base bg-bg-base p-0.5">
          {(
            [
              ['tier', 'Engagement'],
              ['knock', 'Knock status'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setColorBy(key)}
              aria-pressed={colorBy === key}
              className={`rounded px-2 py-1 text-xs font-semibold ${
                colorBy === key
                  ? 'bg-accent text-white'
                  : 'text-fg-secondary hover:text-fg-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <MapLegend
          entries={colorBy === 'knock' ? KNOCK_LEGEND : TIER_LEGEND}
          counts={colorBy === 'knock' ? knock?.counts : undefined}
          className="min-w-0 flex-1"
        />
      </div>

      {colorBy === 'knock' && knockLoading && (
        <div className="text-xs text-fg-muted">Loading knock status…</div>
      )}
      {colorBy === 'knock' && knockError && (
        <div className="rounded-md border border-status-danger/40 bg-status-danger/10 px-3 py-2 text-xs text-status-danger">
          {knockError} Dots are showing engagement colors until this loads.
        </div>
      )}
      {colorBy === 'knock' && (knock?.unknown_voter_ids.length ?? 0) > 0 && (
        <div className="text-xs text-fg-muted">
          {knock!.unknown_voter_ids.length.toLocaleString()} of these doors are
          outside your assigned turf, so their status is not shown.
        </div>
      )}

      <div className="relative rounded-lg overflow-hidden border border-border-base bg-bg-base shadow-lg">
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-bg-base/60 backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-2">
              <div
                className="h-8 w-8 rounded-full border-2 border-status-success-bg/30 border-t-status-success animate-spin"
                aria-label="Loading voters"
              />
              <div className="text-xs text-status-success/80">
                Loading voters…
              </div>
            </div>
          </div>
        )}

        <div style={{ height: 500, width: '100%' }}>
          <MapContainer
            key={mapKey}
            center={center}
            zoom={DEFAULT_ZOOM}
            style={{ width: '100%', height: '100%' }}
            className="z-10"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={19}
            />

            <FullscreenControl />

            <LassoControl
              armed={lassoArmed}
              onArmedChange={setLassoArmed}
              polygon={lassoPolygon}
              onComplete={handleLassoComplete}
            />

            {plotted.length > 0 && (
              <PinClusterLayer
                pins={plotted}
                colorBy={colorBy}
                knockByVoter={knockByVoter}
                onPinClick={setActivePin}
              />
            )}

            {/* One popup for the whole layer, lazy-loading the clicked
                voter's details — pins carry no PII to render eagerly. */}
            {activePin && (
              <Popup
                position={[activePin.lat, activePin.lng]}
                eventHandlers={{ remove: () => setActivePin(null) }}
              >
                <PinPopupContent pin={activePin} />
              </Popup>
            )}
          </MapContainer>
        </div>

        {lassoArmed && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] pointer-events-none">
            <div className="text-xs text-fg-primary bg-bg-elevated/90 backdrop-blur-[1px] px-3 py-1.5 rounded border border-border-base shadow">
              Drag to draw around the voters you want · Esc to cancel
            </div>
          </div>
        )}

        {!loading && plotted.length === 0 && (
          <div className="absolute inset-0 z-[400] flex items-center justify-center pointer-events-none">
            <div className="text-sm text-fg-muted bg-bg-base/70 px-4 py-2 rounded border border-border-base">
              {total === 0
                ? 'No voters match the current filters.'
                : 'No voters with coordinates match the current filters.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoterMapPanel;
